<template>
    <form :class="cn('space-y-4', formClass)" novalidate method="post" @submit.prevent="submit">
        <slot :is-busy :is-valid :form-touched :reset-form />
        <div class="flex gap-4">
            <slot name="actions" :is-busy :is-valid :form-touched :reset-form>
                <Button variant="primary" fill="solid" size="lg" type="submit" :disabled="isBusy || !isValid">
                    <Spinner v-if="isBusy" variant="white" size="xs" />
                    <span>{{ submitText || $t("Global.Submit") }}</span>
                </Button>
                <Button v-if="formTouched || alwaysShowCancel" variant="default" size="lg" @click="resetForm">
                    <span>{{ resetText || $t("Global.Cancel") }}</span>
                </Button>
            </slot>
        </div>
    </form>
</template>
<script lang="ts">
import { safeParse, getDotPath, looseObject, type InferOutput, type ObjectSchema, type ObjectEntries } from "valibot";
import type { HTMLAttributes, InjectionKey } from "vue";
import type { LayoutType } from "~/utils/types/FormFieldProps";

interface FormProps<TSchema extends ObjectSchema<ObjectEntries, undefined>> {
    schema?: TSchema;
    formValues: object;
    isBusy: boolean;
    layout?: LayoutType;
    submitText?: string;
    resetText?: string;
    class?: HTMLAttributes["class"];
    alwaysShowCancel?: boolean;
}

interface FormEmits<TSchema extends ObjectSchema<ObjectEntries, undefined>> {
    submit: [values: InferOutput<TSchema>];
    reset: [];
}

interface FormContext {
    issues: ComputedRef<Record<string, string>>;
    touched: Ref<Record<string, boolean>>;
    formLayout: Ref<LayoutType>;
}

export const FormInjectKey: InjectionKey<FormContext> = Symbol("Form");
</script>
<script setup lang="ts" generic="TSchema extends ObjectSchema<ObjectEntries, undefined>">
const {
    class: formClass = "",
    schema = looseObject({}) as unknown as TSchema,
    isBusy,
    formValues,
    layout = "vertical",
    submitText = "",
    resetText = "",
    alwaysShowCancel = false,
} = defineProps<FormProps<TSchema>>();
const emit = defineEmits<FormEmits<TSchema>>();
const touched = ref<Record<string, boolean>>({});
const formLayout = ref<LayoutType>(layout);

const formTouched = computed(() => Object.keys(touched.value).length > 0);

const parseResult = computed(() => safeParse(schema, formValues));

const isValid = computed(() => parseResult.value.success);

const issues = computed(() => {
    const output: Record<string, string> = {};
    parseResult.value.issues?.forEach((issue) => {
        const field = getDotPath(issue) ?? "";
        if (touched.value[field] === true) output[field] = issue.message;
    });

    return output;
});

function submit() {
    if (isValid.value && !isBusy) emit("submit", parseResult.value.output as InferOutput<TSchema>);
}

function resetForm() {
    touched.value = {};
    emit("reset");
}

provide(FormInjectKey, { issues, touched, formLayout });
defineExpose({ resetForm });
</script>
